import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { formatDate } from "@/lib/blog";

export const SampleBadge = ({ tone = "light" }) => (
  <span
    data-testid="sample-badge"
    className={`inline-flex items-center px-2 py-0.5 text-[10px] tracking-[0.18em] uppercase font-bold border ${
      tone === "dark" ? "border-white/30 text-white/70" : "border-black/20 text-black/50"
    }`}
  >
    Example post
  </span>
);

export const PostMeta = ({ post, className = "" }) => (
  <div className={`flex flex-wrap items-center gap-x-3 gap-y-1 text-xs tracking-[0.18em] uppercase ${className}`}>
    {post.category && <span className="font-bold">{post.category}</span>}
    {post.category && <span className="opacity-30">/</span>}
    <time dateTime={post.published_at}>{formatDate(post.published_at)}</time>
    {post.read_time && (
      <>
        <span className="opacity-30">/</span>
        <span>{post.read_time}</span>
      </>
    )}
    {post.is_sample && <SampleBadge />}
  </div>
);

export const FeaturedPost = ({ post }) => (
  <Link
    to={`/blog/${post.slug}`}
    data-testid={`featured-post-${post.slug}`}
    className="group grid grid-cols-12 gap-y-6 lg:gap-x-10 items-end border-t hairline pt-8"
  >
    <div className="col-span-12 lg:col-span-7 overflow-hidden aspect-[16/10] lg:aspect-[16/9]">
      {post.image_url && (
        <img
          src={post.image_url}
          alt={post.title}
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
          loading="eager"
        />
      )}
    </div>
    <div className="col-span-12 lg:col-span-5 lg:pb-2">
      <PostMeta post={post} className="text-black/55" />
      <h2 className="mt-5 font-display text-3xl sm:text-4xl lg:text-5xl tracking-[-0.02em] leading-[1.02] group-hover:underline underline-offset-8 decoration-1">
        {post.title}
      </h2>
      {post.excerpt && (
        <p className="mt-5 text-black/65 leading-relaxed max-w-lg">{post.excerpt}</p>
      )}
      <span className="mt-6 inline-flex items-center gap-2 text-sm font-medium">
        Read article <ArrowUpRight size={16} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
      </span>
    </div>
  </Link>
);

export const PostRow = ({ post, index }) => (
  <li data-testid={`post-row-${post.slug}`} className="group border-b hairline">
    <Link
      to={`/blog/${post.slug}`}
      className="grid grid-cols-12 gap-x-3 gap-y-3 lg:gap-6 items-center px-2 lg:px-6 py-7 lg:py-9 transition-colors hover:bg-[#0A0A0A] hover:text-white"
    >
      <div className="col-span-2 lg:col-span-1 font-display text-2xl lg:text-3xl text-black/35 group-hover:text-white/50">
        {String(index).padStart(2, "0")}
      </div>
      <div className="col-span-10 lg:col-span-6">
        <div className="text-[11px] tracking-[0.18em] uppercase opacity-55 flex items-center gap-2">
          {post.category && <span>{post.category}</span>}
          {post.category && <span className="opacity-40">/</span>}
          <span>{formatDate(post.published_at)}</span>
          {post.is_sample && <span data-testid="sample-badge" className="font-bold">· Example</span>}
        </div>
        <h3 className="mt-2 font-display text-xl sm:text-2xl lg:text-3xl tracking-tight leading-tight">
          {post.title}
        </h3>
      </div>
      <div className="col-span-10 col-start-3 lg:col-span-3 lg:col-start-auto text-sm opacity-65 line-clamp-2">
        {post.excerpt}
      </div>
      <div className="hidden lg:flex col-span-2 items-center justify-end gap-4">
        {post.image_url && (
          <div className="w-28 h-20 overflow-hidden">
            <img
              src={post.image_url}
              alt=""
              className="w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-105 transition-all duration-500"
              loading="lazy"
            />
          </div>
        )}
        <ArrowUpRight size={18} className="opacity-40 group-hover:opacity-100 transition-opacity" />
      </div>
    </Link>
  </li>
);

export const PostCard = ({ post }) => (
  <Link
    to={`/blog/${post.slug}`}
    data-testid={`related-post-${post.slug}`}
    className="group block border-t hairline pt-5"
  >
    {post.image_url && (
      <div className="aspect-[16/10] overflow-hidden mb-5">
        <img
          src={post.image_url}
          alt=""
          className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-500"
          loading="lazy"
        />
      </div>
    )}
    <div className="text-[11px] tracking-[0.18em] uppercase text-black/50">
      {post.category || "Journal"} · {formatDate(post.published_at)}
    </div>
    <h3 className="mt-2 font-display text-xl lg:text-2xl tracking-tight leading-tight group-hover:underline underline-offset-4 decoration-1">
      {post.title}
    </h3>
  </Link>
);
